import React from "react";
import { Helmet } from "react-helmet-async";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import Header from "components/headers/light.js";
import Footer from "components/footers/SimpleFiveColumn.js";
import { SectionHeading } from "components/misc/Headings";

const HeadingRow = tw.div`flex`;
const Heading = tw(SectionHeading)`text-gray-900 mb-10`;
const Text = styled.div`
  ${tw`text-lg  text-gray-800`}
  p {
    ${tw`mt-2 leading-loose`}
  }
  h1 {
    ${tw`text-3xl font-bold mt-10`}
  }
  h2 {
    ${tw`text-2xl font-bold mt-8`}
  }
  h3 {
    ${tw`text-xl font-bold mt-6`}
  }
  ul {
    ${tw`list-disc list-inside`}
    li {
      ${tw`ml-2 mb-3`}
      p {
        ${tw`mt-0 inline leading-normal`}
      }
    }
  }
`;

export default ({ headingText = "GDPR Policy" }) => {
  return (
    <AnimationRevealPage>
      <Helmet>
        <title>GDPR Compliance | EthosHub</title>
        <meta name="description" content="How EthosHub meets UK and EU GDPR obligations as a processor of sensitive workplace incident data." />
        <link rel="canonical" href="https://www.ethoshub.io/gdpr" />
        <meta property="og:title" content="GDPR Compliance | EthosHub" />
        <meta property="og:description" content="How EthosHub meets UK and EU GDPR obligations as a processor of sensitive workplace incident data." />
        <meta property="og:url" content="https://www.ethoshub.io/gdpr" />
      </Helmet>
      <Header />
      <Container>
        <ContentWithPaddingXl>
          <HeadingRow>
            <Heading>{headingText}</Heading>
          </HeadingRow>
          <Text>
            <h2>Effective Date: 12 June 2025</h2>
            <p>
              Inspired Cloud Solutions Ltd (trading as EthosHub, "we", "our", "us") is committed to protecting
              and respecting your privacy. This GDPR Policy explains how we collect, use, and protect your
              personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data
              Protection Act 2018.
            </p>
            <h2>1. Who We Are</h2>
            <p>
              EthosHub is a digital platform that enables employees to report workplace incidents securely and
              anonymously. Our goal is to help organisations foster safe, respectful work environments.
            </p>
            <p>
              We act as a <strong>Data Processor</strong> for our client organisations (the Data Controllers)
              in respect of employee report data, and as a <strong>Data Controller</strong> in our own right
              for data such as user accounts, billing information, and platform communications.
            </p>
            <h2>2. What Personal Data We Collect</h2>
            <p>Depending on how you interact with our service, we may collect:</p>
            <ul>
              <li>Account information: name, email address, job title, organisation</li>
              <li>Incident report details: free-text descriptions, categories, timestamps, file attachments</li>
              <li>Communication data: messages exchanged with caseworkers</li>
              <li>Usage data: device type, browser, operating system, timestamps</li>
              <li>Anonymous session tokens: a hashed token only — the raw token is never stored, and no IP address is logged for anonymous reporters</li>
            </ul>
            <h2>3. Legal Basis for Processing</h2>
            <p>We process your data on the following legal bases under UK GDPR Article 6:</p>
            <ul>
              <li><strong>Contractual necessity</strong> — to provide the services you or your employer have requested</li>
              <li><strong>Legitimate interests</strong> — for improving service functionality, usability, and security</li>
              <li><strong>Legal obligation</strong> — where required by law, such as data retention or regulatory disclosures</li>
              <li><strong>Consent</strong> — for optional communications such as marketing emails, which you may withdraw at any time</li>
            </ul>
            <p>
              Where incident reports contain Special Category Data (such as information about racial discrimination,
              harassment, or health matters), we process this under Article 9(2)(b) of UK GDPR (processing
              necessary for employment law obligations) and, where applicable, Article 9(2)(g) (substantial
              public interest).
            </p>
            <h2>4. How We Use Your Data</h2>
            <p>We use your personal data to:</p>
            <ul>
              <li>Facilitate the secure reporting and resolution of incidents</li>
              <li>Enable communication between reporters and caseworkers</li>
              <li>Provide analytics to help organisations improve workplace safety</li>
              <li>Monitor and improve platform performance and user experience</li>
              <li>Maintain security and prevent misuse</li>
            </ul>
            <p>We do not sell your personal data or use it for advertising.</p>
            <h2>5. Data Retention</h2>
            <p>
              We retain personal data only as long as necessary to fulfil the purposes for which it was collected.
              Case and incident data is retained in accordance with the subscribing organisation's retention
              settings and applicable employment law. Anonymous session tokens are stored in hashed form only
              and expire after 90 days by default. Payment records are retained as required by HMRC regulations.
            </p>
            <h2>6. Data Sharing and Transfers</h2>
            <p>We may share your data with:</p>
            <ul>
              <li>Your employer or organisation (authorised caseworkers and administrators only)</li>
              <li>Service providers (data processors) who help us deliver the platform, including AWS, Neon, Stripe, and Resend — all bound by strict data processing agreements</li>
              <li>Legal authorities where required by law</li>
            </ul>
            <p>
              Our primary infrastructure is hosted within the UK (AWS eu-west-2). Where data is transferred
              outside the UK, we ensure appropriate safeguards are in place — such as Standard Contractual
              Clauses or UK adequacy decisions — in accordance with UK GDPR Chapter V.
            </p>
            <h2>7. Your Rights Under UK GDPR</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you</li>
              <li>Rectify inaccurate or incomplete data</li>
              <li>Erase your data in certain circumstances ("right to be forgotten")</li>
              <li>Restrict or object to our processing</li>
              <li>Port your data to another service in a structured, machine-readable format</li>
              <li>Withdraw consent at any time where processing is based on consent</li>
              <li>Lodge a complaint with the UK Information Commissioner's Office (ICO) at{" "}
                <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>{" "}
                or by calling 0303 123 1113
              </li>
            </ul>
            <p>
              To exercise any of these rights, contact us at:{" "}
              <a href="mailto:gdpr@ethoshub.io">gdpr@ethoshub.io</a>. We will respond within one month.
            </p>
            <h2>8. Data Security</h2>
            <p>
              We implement appropriate technical and organisational measures to protect your personal data,
              including:
            </p>
            <ul>
              <li>Encryption of data at rest and in transit (TLS)</li>
              <li>Role-based access controls — caseworkers can only access cases within their organisation</li>
              <li>Hashed storage of passwords and anonymous session tokens (raw values are never stored)</li>
              <li>No IP address logging for anonymous reporters</li>
            </ul>
            <p>
              No method of internet transmission or electronic storage is completely secure. We will notify
              you and the relevant authorities of any breach as required by law.
            </p>
            <h2>9. Contact Us</h2>
            <p>If you have any questions or concerns about this policy or your data, please contact us at:</p>
            <p>
              <strong>Inspired Cloud Solutions Ltd</strong> (trading as EthosHub)<br />
              Email: <a href="mailto:gdpr@ethoshub.io">gdpr@ethoshub.io</a><br />
              Website: <a href="https://www.ethoshub.io">www.ethoshub.io</a>
            </p>
            <h2>10. Updates to This Policy</h2>
            <p>
              We may update this policy from time to time. Any changes will be posted on this page with a
              revised "Effective Date". Material changes will be communicated by email or via the platform.
            </p>
          </Text>
        </ContentWithPaddingXl>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};
